import { DataFlowSink, FileNameOption } from 'app/model/dataflow/dataflow-sink';
import { DataFlowExpressionUtils } from '../../expression.util';
import {
    ArrayDataFlowType,
    BooleanDataFlowType,
    IntegerDataFlowType,
    StringDataFlowType
} from 'app/model/dataflow/dataflow-datatype';
import { TransformConvertorBase } from './transform.convertor';
import { ColumnMappingConvertor } from './mapping.convertor';
import { Transformation } from 'app/model/dataflow/transformation';
import { SchemaConvertor } from './schema.convertor';
import { SinkPlainObject } from 'worker/app-workers/shared/plainObjects/sink.plainObject';
import {
    AdapterContext,
    AzureDataExplorerDataset,
    DataFlowParameterConfig,
    IResourceContext,
    Metamodel
} from 'shell-models-lib';
import { TableActionOption } from 'app/model/dataflow/dataset-settings/tabular-type-settings';
import { SqlBaseSettings } from 'app/model/dataflow/dataset-settings/sql-base-settings';
import { DataFlowAdapterUtils } from '../../adapter.util';
import { DeltaSettings } from 'app/model/dataflow/dataset-settings/delta-dataset-settings';
import { DynamicsEntityDataset } from 'app/model/dataset/dynamics-entity.dataset';
import { DynamicsCrmEntityDataset } from 'app/model/dataset/dynamics-crm.dataset';
import { CommonDataServiceEntityDataset } from 'app/model/dataset/common-data-service.dataset';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { DataflowFormatRegistryService } from 'app/registries/dataflow/dataflow-format-registry.service';

export class SinkConvertor extends TransformConvertorBase {
    private get sink(): DataFlowSink {
        return this.transformation as DataFlowSink;
    }

    private get sinkObject(): SinkPlainObject {
        return this.transformObject as SinkPlainObject;
    }

    constructor(sinkObject: SinkPlainObject, sink: DataFlowSink) {
        super(sinkObject, sink);
    }

    public async convert(contextData: IResourceContext): Promise<Transformation> {
        await super.convert(contextData);

        // process inline settings first
        for (const configName of ['store', 'format']) {
            if (this.sinkObject.configs.has(configName)) {
                const expression = this.sinkObject.configs.get(configName);
                const format = DataFlowExpressionUtils.extractStringConfigValue(expression);
                const dataflowFormatRegistryService = await getServiceInstance<DataflowFormatRegistryService>(contextData, 'DataflowFormatRegistryService');
                const modelName = dataflowFormatRegistryService.getInlineSettingsModelName(format, this.sink.sinkLinkedService);
                if (modelName && this.sink.sinkLinkedService) {
                    this._handledConfigs.push(configName);
                    const context = AdapterContext.defaultContextOf(contextData);
                    const adapter = context.dataflowDSLAdapterContext.adapterFor(Metamodel.modelByName(modelName));
                    this.sink.isInlineDataset = true;
                    this.sink.inlineDataSetColumns = SchemaConvertor.convertColumnsFromPlainObject(
                        this.sinkObject.columns,
                        this.sink
                    );
                    this.sink.datasetSettings = await adapter.toModel(
                        this.sinkObject.configs,
                        false,
                        this.sink,
                        this._handledConfigs
                    );
                }
            }
        }
        this.sinkObject.configs.forEach((expression: string, configName: string) => {
            if (this._handledConfigs.includes(configName)) {
                return;
            } else if (configName === 'saveMode') {
                this.sink.savePolicy = expression.substring(1, expression.length - 1);
                this._handledConfigs.push(configName);
            } else if (configName === 'store' && expression === `'cache'`) {
                this.sink.isCacheSink = true;
                this._handledConfigs.push(configName);
                this._handledConfigs.push('format');
            } else if (configName === 'databaseType') {
                this._handledConfigs.push(configName);
                this.sink.isWorkspaceDatabase = true;
                this.sink.workspaceDatabaseType = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink
                );
                this.sink.inlineDataSetColumns = SchemaConvertor.convertColumnsFromPlainObject(
                    this.sinkObject.columns,
                    this.sink
                );
            } else if (configName === 'database') {
                this._handledConfigs.push(configName);
                this.sink.workspaceDatabase = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink
                );
            } else if (configName === 'tableName') {
                this._handledConfigs.push(configName);
                this.sink.workspaceTable = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.sink);
            } else if (configName === 'rowUrlColumn') {
                this.sink.fileNameOption = FileNameOption.RowUrlColumn;
                this.sink.rowUrl = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    StringDataFlowType.TYPE,
                    true
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'rowFolderUrlColumn') {
                this.sink.fileNameOption = FileNameOption.RowFolderUrlColumn;
                this.sink.rowFolderUrlColumn = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    StringDataFlowType.TYPE,
                    true
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'filePattern') {
                this.sink.fileNameOption = FileNameOption.FilePattern;
                this.sink.filePattern = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.sink);
                this._handledConfigs.push(configName);
            } else if (configName === 'partitionFileNames') {
                if (
                    this.sink.partitionFileNames.length === 1 &&
                    this.sink.partitionMethod === 'hash' &&
                    this.sink.numberOfPartitions.expression === '1'
                ) {
                    this.sink.fileNameOption = FileNameOption.SingleFile;
                    this.sink.singleFile = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, this.sink)[0];
                } else {
                    this.sink.fileNameOption = FileNameOption.PartitionFileNames;
                    this.sink.partitionFileNames = DataFlowExpressionUtils.parseExpressionArrayInDsl(
                        expression,
                        this.sink
                    );
                }
                this._handledConfigs.push(configName);
            } else if (configName === 'truncate') {
                this.sink.clearFolder = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this.sink.tableActionOption =
                    expression === 'true' ? TableActionOption.Truncate : this.sink.tableActionOption;
                this._handledConfigs.push(configName);
            } else if (configName === 'quoteAll') {
                this.sink.quoteAll = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'header') {
                this.sink.delimitedTextHeader = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    ArrayDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'recreate') {
                this.sink.tableActionOption =
                    expression === 'true' ? TableActionOption.Recreate : this.sink.tableActionOption;
                this._handledConfigs.push(configName);
            } else if (configName === 'insertable') {
                this.sink.allowInsert = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'updateable') {
                this.sink.allowUpdate = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'deletable') {
                this.sink.allowDelete = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'upsertable') {
                this.sink.allowUpsert = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'alternateKeyName') {
                this.sink.alternateKeyName = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.sink);
                this._handledConfigs.push(configName);
            } else if (configName === 'skipKeyWrites') {
                this.sink.skipKeyWrites = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'batchSize') {
                this.sink.batchSize = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    IntegerDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'ttl') {
                this.sink.ttl = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    IntegerDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'consistencyLevel') {
                this.sink.consistencyLevel = DataFlowExpressionUtils.unescapeSingleQuoteInDslString(
                    DataFlowExpressionUtils.trimSpacesAndQuotesInDslString(expression)
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'timestampFormat') {
                this.sink.dataTypeSettings.timestampFormat = DataFlowExpressionUtils.unescapeSingleQuoteInDslString(
                    DataFlowExpressionUtils.trimSpacesAndQuotesInDslString(expression)
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'dateFormat') {
                this.sink.dataTypeSettings.dateFormat =
                    DataFlowExpressionUtils.trimSpacesAndQuotesInDslString(expression);
                this._handledConfigs.push(configName);
            } else if (configName === 'umask') {
                this.sink.umask = expression.startsWith('(')
                    ? DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.sink)
                    : DataFlowExpressionUtils.unwrapExpressionConfigValue(`'${expression.substring(1)}'`, this.sink);
                this._handledConfigs.push(configName);
            } else if (configName === 'preCommands') {
                const expressionArray = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, this.sink);
                if (expressionArray.length > 0) {
                    this.sink.preFileCommand = expressionArray.map((expression) => {
                        if (!expression.templateExpression) {
                            DataFlowExpressionUtils.unescapeConfigValue(expression);
                        }
                        return expression;
                    });
                }
            } else if (configName === 'postCommands') {
                const expressionArray = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, this.sink);
                if (expressionArray.length > 0) {
                    this.sink.postFileCommand = expressionArray.map((expression) => {
                        if (!expression.templateExpression) {
                            DataFlowExpressionUtils.unescapeConfigValue(expression);
                        }
                        return expression;
                    });
                }
            } else if (configName === 'output') {
                this.sink.cacheSinkOutput = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'booleanFormat') {
                const booleanFormat = DataFlowExpressionUtils.parseStringArrayInDsl(expression);
                this.sink.dataTypeSettings.booleanTrueFormat = booleanFormat[0];
                this.sink.dataTypeSettings.booleanFalseFormat = booleanFormat[1];
                this._handledConfigs.push(configName);
            } else if (configName === 'partitionKey') {
                this.sink.partitionKey = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, this.sink)[0];
                this._handledConfigs.push(configName);
            } else if (configName === 'throughput') {
                this.sink.throughput = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    IntegerDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'writeThroughPutBudget' || configName === 'totalWriteThroughputBudget') {
                this.sink.cosmosUseTotalWriteThroughputBudget = configName === 'totalWriteThroughputBudget';
                this.sink.writeThroughPutBudget = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    IntegerDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'staged') {
                this.sink.staged = expression === 'true';
                this._handledConfigs.push(configName);
            } else if (configName === 'allowCopyCommand') {
                this.sink.useCopyCommandForStaging = expression === 'true';
                this._handledConfigs.push(configName);
            } else if (configName === 'emptyLinesAsHeader') {
                this.sink.emptyLinesAsHeader = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    IntegerDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (
                configName === 'keys' &&
                !(
                    this.sink.datasetSettings &&
                    (this.sink.datasetSettings instanceof SqlBaseSettings ||
                        this.sink.datasetSettings instanceof DeltaSettings)
                )
            ) {
                // The key columns returns an array of expressions.
                this.sink.isListOfKeyColumns = expression.startsWith('[') && expression.endsWith(']');
                this.sink.keyColumns = this.sink.isListOfKeyColumns
                    ? DataFlowExpressionUtils.parseExpressionArrayInDsl(
                          expression,
                          this.sink,
                          StringDataFlowType.TYPE,
                          true
                      )
                    : (this.sink.keyColumns = [
                          DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.sink, 'string[]', true)
                      ]);
                this._handledConfigs.push(configName);
            } else if (configName === 'saveOrder') {
                this.sink.saveOrder = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    IntegerDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'preSQLs') {
                if (expression.startsWith('[') && expression.endsWith(']')) {
                    this.sink.isListOfPreSQLScripts = true;
                    const expressionArray = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, this.sink);
                    if (expressionArray.length > 0) {
                        this.sink.preSQLScript = expressionArray.map((expression) => {
                            if (!expression.templateExpression) {
                                DataFlowExpressionUtils.unescapeConfigValue(expression);
                            }
                            return expression;
                        });
                    }
                } else {
                    this.sink.isListOfPreSQLScripts = false;
                    this.sink.preSQLScript = [
                        DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            expression,
                            this.sink,
                            ArrayDataFlowType.TYPE
                        )
                    ];
                }
                this._handledConfigs.push(configName);
            } else if (configName === 'postSQLs') {
                if (expression.startsWith('[') && expression.endsWith(']')) {
                    this.sink.isListOfPostSQLScripts = true;
                    const expressionArray = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, this.sink);
                    if (expressionArray.length > 0) {
                        this.sink.postSQLScript = expressionArray.map((expression) => {
                            if (!expression.templateExpression) {
                                DataFlowExpressionUtils.unescapeConfigValue(expression);
                            }
                            return expression;
                        });
                    }
                } else {
                    this.sink.isListOfPostSQLScripts = false;
                    this.sink.postSQLScript = [
                        DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            expression,
                            this.sink,
                            ArrayDataFlowType.TYPE
                        )
                    ];
                }
                this._handledConfigs.push(configName);
            } else if (configName === 'timeout') {
                const timeout = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    IntegerDataFlowType.TYPE
                );
                if (
                    this.sink.sinkDataset instanceof AzureDataExplorerDataset ||
                    this.sink.sinkDataset instanceof DynamicsEntityDataset ||
                    this.sink.sinkDataset instanceof DynamicsCrmEntityDataset ||
                    this.sink.sinkDataset instanceof CommonDataServiceEntityDataset
                ) {
                    this.sink.timeout = timeout;
                } else {
                    this.sink.requestTimeout = timeout;
                }
                this._handledConfigs.push(configName);
            } else if (configName === 'requestInterval') {
                this.sink.requestInterval = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    IntegerDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'headers') {
                this.sink.additionalHeaders = expression
                    .slice(1, expression.length - 1)
                    .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER)
                    .map((header) => {
                        const splits = header.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
                        const dataFlowParameterConfig = new DataFlowParameterConfig();
                        dataFlowParameterConfig.paramName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            splits[0].trim(),
                            this.sink
                        );
                        dataFlowParameterConfig.paramValue = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            splits[1].trim(),
                            this.sink
                        );
                        return dataFlowParameterConfig;
                    });
                this._handledConfigs.push(configName);
            } else if (configName === 'queryParameters') {
                this.sink.queryParameters = expression
                    .slice(1, expression.length - 1)
                    .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER)
                    .map((para) => {
                        const splits = para.split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER);
                        const dataFlowParameterConfig = new DataFlowParameterConfig();
                        dataFlowParameterConfig.paramName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            splits[0].trim(),
                            this.sink
                        );
                        dataFlowParameterConfig.paramValue = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                            splits[1].trim(),
                            this.sink
                        );
                        return dataFlowParameterConfig;
                    });
                this._handledConfigs.push(configName);
            } else if (configName === 'httpCompressionType') {
                this.sink.httpCompressionType = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'requestFormat') {
                const formatSettings = expression
                    .slice(1, expression.length - 1)
                    .split(DataFlowAdapterUtils.MAP_ARRAY_SPLITTER);
                formatSettings.forEach((setting) => {
                    const [settingName, settingValue] = setting
                        .split(DataFlowAdapterUtils.MAP_KEY_VALUE_SPLITTER)
                        .map((v) => v.trim());
                    switch (settingName) {
                        case `'type'`:
                            this.sink.restDataFormat = DataFlowExpressionUtils.extractStringConfigValue(settingValue);
                            break;
                        case `'outputDateFormat'`:
                            this.sink.dateFormat = DataFlowExpressionUtils.extractStringConfigValue(settingValue);
                            break;
                        case `'outputTimestampFormat'`:
                            this.sink.timestampFormat = DataFlowExpressionUtils.extractStringConfigValue(settingValue);
                            break;
                        default:
                    }
                });
                this._handledConfigs.push(configName);
            } else if (configName === 'rowRelativeUrl') {
                this.sink.rowRelativeUrl = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.sink);
                this._handledConfigs.push(configName);
            } else if (configName === 'skipRowRelativeUrl') {
                this.sink.skipRowRelativeUrl = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'insertHttpMethod') {
                this.sink.insertHttpMethod = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.sink);
                this._handledConfigs.push(configName);
            } else if (configName === 'deleteHttpMethod') {
                this.sink.deleteHttpMethod = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.sink);
                this._handledConfigs.push(configName);
            } else if (configName === 'upsertHttpMethod') {
                this.sink.upsertHttpMethod = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.sink);
                this._handledConfigs.push(configName);
            } else if (configName === 'updateHttpMethod') {
                this.sink.updateHttpMethod = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, this.sink);
                this._handledConfigs.push(configName);
            } else if (configName === 'stagingSchemaName') {
                this.sink.stagingSchemaName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink
                );
                this.sink.useTmpDB = false;
                this._handledConfigs.push(configName);
            } else if (configName === 'errorHandlingOption') {
                this.sink.enableErrorHandling = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    StringDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'reportSuccessOnError') {
                this.sink.reportSuccessOnError = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'outputAssertFailedRows') {
                this.sink.outputAssertFailedRowsToAltStorage = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'ignoreAssertFailedRows') {
                this.sink.ignoreAssertFailedRows = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'outputRejectedData') {
                this.sink.outputRejectedData = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'transactionCommit') {
                this.sink.dataVisibility = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    StringDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'rejectedData_container') {
                this.sink.sqlErrorContainer = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    StringDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'rejectedData_fileSystem') {
                this.sink.sqlErrorFileSystem = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    StringDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'rejectedData_folderPath') {
                this.sink.sqlErrorFolderPath = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    StringDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'assertFailure_container') {
                this.sink.assertFailureContainer = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    StringDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'assertFailure_fileSystem') {
                this.sink.assertFailureFileSystem = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    StringDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'assertFailure_folderPath') {
                this.sink.assertFailureFolderPath = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    StringDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'stageInsert') {
                this.sink.snowflakeStageInsert = expression === 'true';
                this._handledConfigs.push(configName);
            } else if (configName === 'validateSchema') {
                this.sink.validateSchema = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'allowSchemaDrift') {
                this.sink.allowSchemaDrift = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this.sink.hasSchemaDriftAttribute = true;
                this._handledConfigs.push(configName);
            } else if (configName === 'mergeSchema') {
                this.sink.mergeSchema = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'autoCompact') {
                this.sink.autoCompact = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else if (configName === 'optimizedWrite') {
                this.sink.optimizedWrite = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    expression,
                    this.sink,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(configName);
            } else {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (this.sink as any)[configName] = expression === 'true';
            }
        });

        ColumnMappingConvertor.convertFromPlainObject(this.sinkObject, this.sink, this._handledConfigs);
        super.postConvert();
        return this.sink;
    }
}
